import isArray from 'lodash/isArray';
import tail from 'lodash/tail';
import sumBy from 'lodash/sumBy';
import round from 'lodash/round';

import {
  FunnelQueryResponse,
  isSegmentedData,
  SegmentedFunnelResults,
  UnsegmentedFunnelResult,
} from 'common/report/funnels/models/api-types';
import {SegmentItem, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {
  BasicFunnelStep,
  BucketData,
  ChartType,
  FunnelBookmark,
  FunnelsAggregateType,
  FunnelStep,
} from 'common/report/funnels/models/types';
import {BucketDefinition} from 'common/report/funnels/models/bar-chart/types';
import {
  Bar,
  BarPattern,
  BarSegment,
  Segment as BarVizSegment,
  SegmentWithChildren,
  SegmentWithoutChildren,
} from 'common/charts/visualization/bar-viz/types';
import {
  getBucketData,
  getBucketFromBookmarkFormat,
  getFunnelStep,
  percentOfTotal,
} from 'common/report/funnels/models/bar-chart/utils';
import {formatDuration, getPrecision} from 'common/util/date';
import * as colors from 'common/util/colors';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/funnels/models/constants';
import {ChartVizType, SavedLegend, ValueRepresentationType} from 'common/report/constants';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {
  conversionMode,
  getBucketLabel as frequencyBucketLabeler,
  getMedianFormatter as frequencyMedianFormatter,
} from 'common/report/funnels/models/frequency-chart/utils';
import {
  getBucketAccessor as ttcBucketAccessor,
  getBucketLabel as ttcBucketLabeler,
  getMedianAccessor,
  getMedianFormatter as ttcMedianFormatter,
  getTTCShiftedEventIndex,
} from 'common/report/funnels/models/time-to-convert/utils';
import {addConversionRateToFunnelStep, collectNestedSegments, getSegmentColumnSpecs} from 'common/report/funnels/util';
import {
  FlatSortConfig,
  OrderedSumTree,
  SortBy,
  SortConfig,
  SortOrder,
  UnorderedSumTree,
} from 'common/report/util/sorting/types';
import {deepMapValuesToZero, sortSumTree} from 'common/report/util/sorting/utils';
import {LiftType} from 'common/report/util/lift/types';

import {convertToNonFlatSavedLegend} from './colors';

export interface BucketValueInfo {
  count: number;
  percentage: number;
  bucketIdx: number;
  isOutsideBound: boolean;
  liftComparisonCount?: number;
  segmentName?: string;
  baselineSegmentName?: string;
}

export interface CollapsedBucket {
  count: number;
  percentage: number;
  median?: number;
  bucketIdx: number;
  liftComparisonCount?: number;
}

type SegmentKeyAndLabel = {id: string; label: string};

export interface FunnelSegmentExtras {
  segmentPath: SegmentKeyAndLabel[];
  isComparison: boolean;
  baselineSegmentName?: string;
}

function createBarChartUnorderedSumTree(
  series: UnsegmentedFunnelResult | SegmentedFunnelResults,
  timeComparisonSeries: SegmentedFunnelResults,
  bookmark: FunnelBookmark,
  currPath: string[] = [],
): UnorderedSumTree {
  const keys = Array.from(new Set([...Object.keys(series), ...Object.keys(timeComparisonSeries ?? {})]));

  return keys.reduce((acc, key) => {
    const path = [...currPath, key];

    // if key is not present in the main series but is in the time comparison series,
    // pad the main series with zero values from the time comparison series.
    if (!Object.prototype.hasOwnProperty.call(series, key)) {
      series[key] = deepMapValuesToZero(timeComparisonSeries[key]);
    }

    const leafValue = getValueIfLeaf(series[key], bookmark);
    if (leafValue !== null) {
      acc[key] = {
        label: key,
        value: leafValue,
        timeComparisonValue: getValueIfLeaf(timeComparisonSeries?.[key] ?? {}, bookmark)
          ? getValueIfLeaf(timeComparisonSeries?.[key] ?? {}, bookmark)
          : 0,
        path,
      };
    } else {
      const children = createBarChartUnorderedSumTree(series[key], {}, bookmark, path);

      acc[key] = {
        label: key,
        value: sumBy(Object.values(children), `value`),
        timeComparisonValue: sumBy(Object.values(children), `timeComparisonValue`),
        children,
        path,
      };
    }

    return acc;
  }, {});
}

function getValueIfLeaf(
  series: UnsegmentedFunnelResult | SegmentedFunnelResults | Array<BasicFunnelStep | FunnelStep>,
  bookmark: FunnelBookmark,
): number | null {
  const stepIndex = getStepIndexFromBookmark(bookmark);
  if (isArray(series)) {
    return series[stepIndex ? stepIndex : series.length - 1]?.count;
  }
  return null;
}

function createSortedMap(
  sortedMap: Map<string, {value: number; children: Map<string, TODO>}>,
  sortedTree: OrderedSumTree[],
): Map<string, {value: number; children: Map<string, any>}> {
  for (const tree of sortedTree) {
    let childSortMap = new Map();
    if (tree.children) {
      childSortMap = createSortedMap(childSortMap, tree.children);
    }
    const index = sortedTree.indexOf(tree);
    sortedMap.set(tree.label, {value: index, children: childSortMap});
  }
  return sortedMap;
}

function mapBasedSort(
  segments: Array<BarVizSegment<FunnelSegmentExtras>>,
  sortedMap: Map<string, {value: number; children: Map<string, TODO>}>,
): Array<BarVizSegment<FunnelSegmentExtras>> {
  segments.sort((a, b) => {
    const matchOne = sortedMap.get(a[`label`]);
    const matchTwo = sortedMap.get(b[`label`]);
    if (!matchOne.value || matchOne.value < matchTwo.value) {
      return -1;
    } else if (!matchTwo.value || matchOne.value > matchTwo.value) {
      return 1;
    }
    return 0;
  });
  return segments;
}

function sortSegmentsUsingSortedMap(
  segments: Array<BarVizSegment<FunnelSegmentExtras>>,
  sortedMap: Map<string, {value: number; children: Map<string, TODO>}>,
): Array<BarVizSegment<FunnelSegmentExtras>> {
  for (const segment of segments) {
    let childArray;
    const match = sortedMap.get(segment[`label`]);
    if (match.children?.size > 0) {
      childArray = sortSegmentsUsingSortedMap(segment[`children`], match.children);
      segment[`children`] = childArray;
    }
  }
  const finalArray = mapBasedSort(segments, sortedMap);
  return finalArray;
}

export function sortSegments(
  segments: Array<BarVizSegment<FunnelSegmentExtras>>,
  funnelsResponse: FunnelQueryResponse,
  bookmark: FunnelBookmark,
  sortConfig: SortConfig,
): void {
  const segmentData = funnelsResponse.data[funnelsResponse.meta.dates[0]];
  const compareData = funnelsResponse.compare_data
    ? (Object.values(funnelsResponse.compare_data)[0] as SegmentedFunnelResults)
    : null;
  if (isSegmentedData(segmentData)) {
    const sumTree = createBarChartUnorderedSumTree(segmentData, compareData, bookmark);
    const sortedTree = sortSumTree(sumTree, sortConfig);
    const sortedTreeMap = createSortedMap(new Map(), sortedTree);
    sortSegmentsUsingSortedMap(segments, sortedTreeMap);
  }
}

export function getDefaultSortConfig(segmentationLevel: number): SortConfig {
  const DEFAULT_SORT_ATTRS: FlatSortConfig = {sortBy: SortBy.Value, sortOrder: SortOrder.Descending};
  const DEFAULT_SORT_CONFIG: SortConfig = {
    sortBy: SortBy.Column,
    colSortAttrs: Array(segmentationLevel).fill(DEFAULT_SORT_ATTRS),
  };
  return DEFAULT_SORT_CONFIG;
}

export function getBucketCount(bucketData: {buckets: any; higher: any; lower: any}, index: number): number {
  const buckets = bucketData.buckets;
  let count = buckets.length > 0 ? buckets[index] : 0;
  //index=-1 for overall lower and index=buckets.length for overall higher
  if (index < 0 || index >= buckets.length) {
    count = index >= buckets.length ? bucketData.higher : bucketData.lower;
  }
  return count;
}

export function getBucketMedian(bucketData: {buckets: any; higher: any; lower: any}, index: number): number {
  const buckets = bucketData.buckets;
  let median = buckets.length > 0 ? buckets[index] : 0;
  //index=-1 for overall lower and index=buckets.length for overall higher
  if (index < 0 || index >= buckets.length) {
    median = 0;
  }
  return median;
}

export function getStepIndexFromBookmark(bookmark: FunnelBookmark): number | null {
  switch (bookmark.chart_type) {
    case ChartType.Frequency:
      return bookmark.eventIndex || 0;
    case ChartType.TimeToConvert:
      return getTTCShiftedEventIndex(bookmark.eventIndex);
    default:
      throw new Error(`Unsupported chart type for bookmark step index`);
  }
}

function bookmarkIsRelative(bookmark: FunnelBookmark): boolean {
  switch (bookmark.chart_type) {
    case ChartType.Frequency:
      return (
        bookmark.frequencyType === FrequencyViewType.ConversionRate ||
        bookmark.frequencyValueRepresentation === ValueRepresentationType.Relative
      );
    case ChartType.TimeToConvert:
      return bookmark.ttcValueRepresentation === ValueRepresentationType.Relative;
    default:
      return false;
  }
}

function isMedianChart(bookmark: FunnelBookmark): boolean {
  switch (bookmark.chart_type) {
    case ChartType.Frequency:
      return (
        bookmark.frequencyAggregateType === FunnelsAggregateType.Median &&
        bookmark.frequencyVizType === ChartVizType.Bar
      );
    case ChartType.TimeToConvert:
      return bookmark.ttcAggregateType === FunnelsAggregateType.Median && bookmark.ttcVizType === ChartVizType.Bar;
    default:
      return false;
  }
}

function getBucketDefinitionAndAccessorKeysFromBookmark(
  bookmark: FunnelBookmark,
): {bucketAccessor: keyof FunnelStep; bucketDefinition: BucketDefinition} {
  if (bookmark.chart_type === ChartType.TimeToConvert) {
    return {
      bucketAccessor:
        bookmark.ttcAggregateType === FunnelsAggregateType.Distribution
          ? ttcBucketAccessor(getStepIndexFromBookmark(bookmark))
          : getMedianAccessor(getStepIndexFromBookmark(bookmark)),
      bucketDefinition: getBucketFromBookmarkFormat(bookmark.time_buckets),
    };
  }
  return {
    bucketAccessor:
      bookmark.frequencyAggregateType === FunnelsAggregateType.Distribution
        ? conversionMode(bookmark.frequencyType).bucketAccessor
        : conversionMode(bookmark.frequencyType).medianAccessor,
    bucketDefinition: getBucketFromBookmarkFormat(bookmark.frequency_buckets),
  };
}

export function bookmarkHasTimeComparison(bookmark: FunnelBookmark): boolean {
  return !!bookmark.time_comparison;
}

export function bookmarkHasLiftComparison(bookmark: FunnelBookmark): boolean {
  return !!bookmark.liftComparison;
}

function getBucketDataFromSegment(
  segment: FunnelStep[],
  stepIndex: number,
  bucketAccessor,
  isMedian: boolean,
): BucketData {
  const step = getFunnelStep(segment, stepIndex);
  return getBucketData(step, bucketAccessor, isMedian);
}

function getValuesForBucket(
  bucketData: BucketData,
  index: number,
  showCountAsPercentage: boolean,
): {
  count: any;
  percentage: number;
  median?: number;
} {
  const count = getBucketCount(bucketData, index);
  const percentage = percentOfTotal(bucketData, count, showCountAsPercentage);
  const median = getBucketMedian(bucketData, index);

  return {count, percentage, median};
}

function getValuesForLiftBucket(
  bucketData: BucketData,
  index: number,
): {
  count: any;
  percentage: number;
  median?: number;
} {
  const count = getBucketCount(bucketData, index);
  const percentage = round(count * 100, 2);

  return {count, percentage};
}

/**
 *  Generates BarViz segments from funnel results and a bookmark
 * @param segments - Tree of segmented funnel steps
 * @param compareSegments - Tree of the same shape as the segments with compare data at each leaf corresponding, this can be either time comparison or baseline comparison
 * @param selectedSegmentKeys - In order of tree traversal, so that the first element corresponds to the first depth of the tree
 * e.g. for keys: [ [NYC, Seattle], [OSX, Windows] ], segments should look like:
 * {
 *   NYC: {
 *     OSX: <step>,
 *     Windows: <step>
 *   }
 *   Seattle: {
 *     OSX: <step>,
 *     Windows: <step>
 *   }
 * }
 *
 * @param bookmark - The query used to generate the funnel results
 * @param segmentPath - current keys used to traverse the result tree to get to this node
 */
export function generateBarsFromFunnelSegmentTree({
  segments,
  compareSegments,
  liftSegments,
  selectedSegmentKeys,
  bookmark,
  segmentPath,
}: {
  segments: SegmentedFunnelResults;
  compareSegments?: SegmentedFunnelResults;
  liftSegments?: SegmentedFunnelResults;
  selectedSegmentKeys: SegmentSection[];
  bookmark: FunnelBookmark;
  segmentPath: SegmentKeyAndLabel[];
}): BarVizSegment[] {
  // Always grab the first segment group which matches this depth of segments
  const segmentsToSearchFor = selectedSegmentKeys[0]?.items;
  if (!segmentsToSearchFor) {
    return [];
  }

  const segmentChildren: BarVizSegment[] = [];

  let liftSegment, baselineSegmentName;
  let compareSegment;
  if (bookmark.liftComparison?.type === LiftType.Segment || bookmark.liftComparison?.type === LiftType.Overall) {
    const {baselineSegment, baselineSegmentName: baselineSegmentLabel} = getBaselineAndPercentOverallInfo(
      segments,
      liftSegments,
      bookmark,
    );
    compareSegment = baselineSegment;
    baselineSegmentName = baselineSegmentLabel;
  }

  for (let i = 0; i < segmentsToSearchFor.length; i++) {
    const segmentKey = segmentsToSearchFor[i];
    const resultingSegment = segments[segmentKey.id];

    if (bookmarkHasLiftComparison(bookmark)) {
      liftSegment = liftSegments[segmentKey.id] as FunnelStep[];
    }

    if (bookmarkHasTimeComparison(bookmark)) {
      compareSegment = compareSegments[segmentKey.id] as FunnelStep[];
    }

    if (isArray(resultingSegment)) {
      // arrays are termination steps, return this path key
      const leafSegment = makeLeafDistributionSegment({
        segment: resultingSegment,
        compareSegment,
        liftSegment,
        baselineSegmentName,
        bookmark,
        segmentKey,
        segmentPath,
        selectedSegmentKeys,
      });
      if (leafSegment) {
        segmentChildren.push(leafSegment);
      }
    } else if (resultingSegment) {
      const subChildren = generateBarsFromFunnelSegmentTree({
        segments: resultingSegment,
        compareSegments: compareSegments ? (compareSegments[segmentKey.id] as SegmentedFunnelResults) : null,
        liftSegments: liftSegments ? (liftSegments[segmentKey.id] as SegmentedFunnelResults) : null,
        selectedSegmentKeys: tail(selectedSegmentKeys),
        bookmark,
        segmentPath: segmentPath.concat(segmentKey),
      });

      if (subChildren.length) {
        segmentChildren.push({
          segmentName: selectedSegmentKeys[0].label,
          label: segmentKey.id,
          children: subChildren,
        });
      }
    }
  }

  return segmentChildren;
}

function getBaselineAndPercentOverallInfo(
  segments: SegmentedFunnelResults,
  liftSegments: SegmentedFunnelResults,
  bookmark: FunnelBookmark,
) {
  let baselineSegment;
  let baselineSegmentName;
  if (liftSegments) {
    if (bookmark.liftComparison?.type === LiftType.Segment) {
      baselineSegmentName = bookmark.liftComparison?.groupValue;
      baselineSegment = segments[bookmark.liftComparison?.groupValue];
    } else if (bookmark.liftComparison?.type === LiftType.Overall) {
      // $overall only comes from query-api at the top-level, as of now
      baselineSegmentName = `Overall`;
      baselineSegment = segments[OVERALL_PROP];
    }
  }
  return {baselineSegment, baselineSegmentName};
}

function makeLeafDistributionSegment({
  segment,
  compareSegment,
  liftSegment,
  baselineSegmentName,
  segmentKey,
  bookmark,
  segmentPath,
  selectedSegmentKeys,
}: {
  segment: FunnelStep[];
  compareSegment: FunnelStep[];
  liftSegment: FunnelStep[];
  baselineSegmentName?: string;
  segmentKey: SegmentItem;
  bookmark: FunnelBookmark;
  segmentPath: SegmentKeyAndLabel[];
  selectedSegmentKeys: SegmentSection[];
}): BarVizSegment {
  const distributionSegments = generateLeafSegment({
    segment,
    compareSegment,
    liftSegment,
    baselineSegmentName,
    color: segmentKey.color,
    bookmark,
    segmentLabel: segmentKey.label,
    segmentPath: segmentPath.concat(segmentKey),
  });

  if (distributionSegments.length) {
    // If we are working with a median chart, the bars will be one level up since we do not have the additional distribution column.
    const segmentObj = !isMedianChart(bookmark)
      ? {children: distributionSegments}
      : {bars: distributionSegments[0].bars, extras: distributionSegments[0].extras};

    const segmentParent: SegmentWithChildren | SegmentWithoutChildren = {
      ...segmentObj,
      label: segmentKey.label,
      segmentName: selectedSegmentKeys[0].label,
    };

    return segmentParent;
  } else {
    return null;
  }
}

export function generateLeafSegment({
  segment,
  compareSegment,
  liftSegment,
  baselineSegmentName,
  color,
  bookmark,
  segmentLabel,
  segmentPath,
}: {
  segment: FunnelStep[];
  compareSegment?: FunnelStep[];
  liftSegment?: FunnelStep[];
  baselineSegmentName?: string;
  color: string;
  bookmark: FunnelBookmark;
  segmentLabel: string;
  segmentPath: SegmentKeyAndLabel[];
}): Array<SegmentWithoutChildren<FunnelSegmentExtras>> {
  const hasLift = bookmarkHasLiftComparison(bookmark);
  const isRelative = bookmarkIsRelative(bookmark) || hasLift;
  const isMedian = isMedianChart(bookmark);
  const showCountAsPercentage =
    (bookmark.chart_type === ChartType.Frequency && bookmark.frequencyType === FrequencyViewType.ConversionRate) ||
    hasLift;
  const liftType = bookmark.liftComparison?.type;

  const getBucketLabel = bookmark.chart_type === ChartType.Frequency ? frequencyBucketLabeler : ttcBucketLabeler;
  const medianFormatter = bookmark.chart_type === ChartType.Frequency ? frequencyMedianFormatter : ttcMedianFormatter;
  const stepIndex = getStepIndexFromBookmark(bookmark);
  const {bucketDefinition, bucketAccessor} = getBucketDefinitionAndAccessorKeysFromBookmark(bookmark);

  const bucketData = getBucketDataFromSegment(segment, stepIndex, bucketAccessor, isMedian);

  const buckets = getDistributionForSegment({
    bucket: bucketData,
    compareBucket: compareSegment
      ? getBucketDataFromSegment(compareSegment, stepIndex, bucketAccessor, isMedian)
      : null,
    liftBucket: liftSegment ? getBucketDataFromSegment(liftSegment, stepIndex, bucketAccessor, isMedian) : null,
    bucketDefinition,
    getBucketLabel,
    showCountAsPercentage,
    isRelative,
    isMedian,
    liftType,
  });

  let compareBuckets = [];
  if (compareSegment && !liftSegment) {
    const compareBucketData = getBucketDataFromSegment(compareSegment, stepIndex, bucketAccessor, isMedian);

    //TODO: (Bruna) restructure this in the future so that blobs of processing code are not just being randomly added
    if (
      compareBucketData.buckets.length <= 0 ||
      (compareBucketData.buckets.length !== bucketData.buckets.length &&
        compareBucketData.buckets.every((item) => item === 0))
    ) {
      compareBucketData.buckets = Array(bucketData.buckets.length).fill(0);
    }

    compareBuckets = getDistributionForSegment({
      bucket: compareBucketData,
      compareBucket: bucketData,
      bucketDefinition,
      getBucketLabel,
      showCountAsPercentage,
      isRelative,
      isMedian,
    });
  }

  return buckets.map(({value, label, extras}, idx) => {
    const bars: Array<Bar<FunnelSegmentExtras>> = [
      {
        segments: [
          {
            value,
            label: formatLabel(value, isRelative, isMedian, medianFormatter),
            color,
            extras: {
              ...extras,
              isComparison: false,
              segmentPath,
              baselineSegmentName,
            },
          },
        ],
        extras: {
          isComparison: false,
          segmentPath,
        },
      },
    ];

    if (compareSegment && !liftSegment) {
      const {value: compareValue, extras} = compareBuckets[idx];
      bars.push({
        segments: [
          {
            pattern: BarPattern.Striped,
            value: compareValue,
            label: formatLabel(compareValue, isRelative, isMedian, medianFormatter),
            color,
            extras: {
              ...extras,
              isComparison: true,
            },
          },
        ],
        extras: {
          isComparison: true,
          segmentPath,
        },
      });
    }

    return {
      segmentName: isMedian ? null : bookmark.chart_type === ChartType.Frequency ? `Frequency` : `Time Span`,
      label: label === null && segmentLabel ? segmentLabel : label,
      bars,
      extras: {
        parentLabel: segmentLabel,
        isComparison: bars.length > 1,
        segmentPath,
      },
    };
  });
}

function formatLabel(
  value: number,
  isRelative: boolean,
  isMedian: boolean,
  medianFormatter: {(median: number): string; (arg0: number): any},
) {
  if (isMedian) {
    return medianFormatter ? medianFormatter(value) : value.toString();
  } else {
    return isRelative ? `${value}%` : String(value);
  }
}

function createCollapsedBuckets({
  bucket,
  compareBucket,
  liftBucket,
  showCountAsPercentage,
}: {
  bucket: BucketData;
  compareBucket?: BucketData;
  liftBucket?: BucketData;
  baselineBucket?: BucketData;
  showCountAsPercentage: boolean;
}): CollapsedBucket[] {
  const {buckets} = bucket;
  const collapsedBuckets: CollapsedBucket[] = [];
  let canAddLeadingZeroBucket = true;

  for (let bucketIdx = 0; bucketIdx < buckets.length; bucketIdx++) {
    const {count: bucketCount, percentage, median} = getValuesForBucket(bucket, bucketIdx, showCountAsPercentage);

    if (canAddLeadingZeroBucket && bucketCount === 0) {
      collapsedBuckets.push({count: 0, percentage: 0, median: 0, bucketIdx});
      canAddLeadingZeroBucket = false;
    } else if (bucketCount !== 0) {
      if (liftBucket) {
        const {percentage: liftPercentage} = getValuesForLiftBucket(liftBucket, bucketIdx);

        const liftComparisonCount = getValuesForBucket(compareBucket, bucketIdx, showCountAsPercentage).count;

        collapsedBuckets.push({
          count: bucketCount,
          liftComparisonCount,
          percentage: liftPercentage,
          median,
          bucketIdx,
        });
      } else {
        collapsedBuckets.push({count: bucketCount, percentage, median, bucketIdx});
      }
      canAddLeadingZeroBucket = true;
    } else if (compareBucket) {
      const previousCompareBucketHadData =
        bucketIdx > 0 && getValuesForBucket(compareBucket, bucketIdx - 1, showCountAsPercentage).count !== 0;
      const compareBucketHasData = getValuesForBucket(compareBucket, bucketIdx, showCountAsPercentage).count !== 0;

      // Ensure we don't skip this zero bucket IF the compare bucket had data in the previous step OR this step
      // This guarantees we don't collapse buckets that have data
      if (compareBucketHasData || previousCompareBucketHadData) {
        collapsedBuckets.push({count: 0, percentage: 0, median: 0, bucketIdx});
      }
    }
  }

  return collapsedBuckets;
}

/** Gets the distribution for a specific bucket, while collapsing adjacent buckets
 * e.g. if the data for a segment is
 * 1: 0
 * 2: 12
 * 3 0
 * Then it will return values and labels for [1: 0, 2: 12, 3: 0]
 */
export function getDistributionForSegment({
  bucket,
  compareBucket,
  liftBucket,
  bucketDefinition,
  getBucketLabel,
  showCountAsPercentage,
  isRelative,
  isMedian = false,
  liftType,
}: {
  bucket: BucketData;
  compareBucket?: BucketData;
  liftBucket?: BucketData;
  bucketDefinition: BucketDefinition;
  getBucketLabel: (bucketStart: number, bucketEnd?: number, precision?: number) => string;
  showCountAsPercentage: boolean;
  isRelative: boolean;
  isMedian?: boolean;
  liftType?: LiftType;
}): Array<BarSegment<BucketValueInfo>> {
  const {bucketSize, min: bucketMin, max: bucketMax} = bucketDefinition;

  const collapsedBuckets = createCollapsedBuckets({
    bucket,
    compareBucket,
    liftBucket,
    showCountAsPercentage,
  });

  const bucketSegments: Array<BarSegment<BucketValueInfo>> = [];

  for (let idx = 0; idx < collapsedBuckets.length; idx++) {
    const bucketStart = bucketMin + collapsedBuckets[idx].bucketIdx * bucketSize;

    let bucketEnd;
    // Last bucket's end should always be the max bucket or the "overflow" from the next section
    if (idx === collapsedBuckets.length - 1) {
      bucketEnd = Math.max(bucketMax, bucketMin + (collapsedBuckets[idx].bucketIdx + 1) * bucketSize);
    } else {
      bucketEnd = bucketMin + collapsedBuckets[idx + 1].bucketIdx * bucketSize;
    }

    const endTime = bucketStart + bucketSize * bucketSize;
    const endUnit = formatDuration(endTime * 1000, 0).unit;
    const precision = getPrecision(endUnit, bucketSize * 1000);

    const bucketLabel = !isMedian ? getBucketLabel(bucketStart, bucketEnd, precision) : null;

    let value: number;
    if (isMedian) {
      value = collapsedBuckets[idx].median;
    } else {
      value = isRelative ? collapsedBuckets[idx].percentage : collapsedBuckets[idx].count;
    }

    bucketSegments.push({
      value,
      label: bucketLabel,
      extras: {
        count: collapsedBuckets[idx].count,
        percentage: collapsedBuckets[idx].percentage,
        bucketIdx: collapsedBuckets[idx].bucketIdx,
        ...(collapsedBuckets[idx].liftComparisonCount
          ? {liftComparisonCount: collapsedBuckets[idx].liftComparisonCount}
          : {}),
        isOutsideBound: false,
      },
    });
  }

  // Add higher and lower values if needed
  addHigherAndLowerBuckets({
    bucketSegments,
    bucket,
    compareBucket,
    liftBucket,
    bucketMin,
    bucketMax,
    getBucketLabel,
    showCountAsPercentage,
    isRelative,
    liftType,
  });

  if (
    bucketSegments.every((bucket) => bucket.value === 0) &&
    compareBucket?.buckets.every((value) => value === 0) &&
    compareBucket?.lower === 0 &&
    compareBucket?.higher === 0
  ) {
    return [];
  }

  return bucketSegments;
}

function addHigherAndLowerBuckets({
  bucketSegments,
  bucket,
  compareBucket,
  liftBucket,
  bucketMin,
  bucketMax,
  getBucketLabel,
  showCountAsPercentage,
  isRelative,
  liftType,
}: {
  bucketSegments: Array<BarSegment<BucketValueInfo>>;
  bucket: BucketData;
  compareBucket?: BucketData;
  liftBucket?: BucketData;
  bucketMin: number;
  bucketMax: number;
  getBucketLabel: (bucketStart: number, bucketEnd?: number, precision?: number) => string;
  showCountAsPercentage: boolean;
  isRelative: boolean;
  liftType?: LiftType;
}): void {
  function getHigherLowerBucketSegment(idx) {
    let {count, percentage} = getValuesForBucket(bucket, idx, showCountAsPercentage);

    let liftComparisonCount;
    if (liftType) {
      percentage = getValuesForLiftBucket(liftBucket, idx).percentage;
      liftComparisonCount = getValuesForBucket(compareBucket, idx, showCountAsPercentage).count;
    }

    return {count, percentage, liftComparisonCount};
  }

  if (bucket.lower || compareBucket?.lower) {
    const bucketLabel = getBucketLabel(bucketMin);
    const {count, percentage, liftComparisonCount} = getHigherLowerBucketSegment(-1);

    bucketSegments.unshift({
      value: isRelative ? percentage : count,
      label: `< ${bucketLabel}`,
      extras: {
        count,
        percentage,
        ...(liftComparisonCount ? {liftComparisonCount} : {}),
        bucketIdx: -1,
        isOutsideBound: true,
      },
    });
  }

  if (bucket.higher || compareBucket?.higher) {
    const bucketLabel = getBucketLabel(bucketMax);
    const {count, percentage, liftComparisonCount} = getHigherLowerBucketSegment(bucket.buckets.length);

    bucketSegments.push({
      value: isRelative ? percentage : count,
      label: `> ${bucketLabel}`,
      extras: {
        count,
        percentage,
        ...(liftComparisonCount ? {liftComparisonCount} : {}),
        bucketIdx: bucket.buckets.length,
        isOutsideBound: true,
      },
    });
  }
}

export function generateLegendSections(
  funnelsResponse: FunnelQueryResponse,
  bookmark: FunnelBookmark,
): {sections: SegmentSection[]; savedLegend: SavedLegend[]} {
  const segmentData = funnelsResponse.data[funnelsResponse.meta.dates[0]];

  if (isSegmentedData(segmentData)) {
    const labels: string[] = getSegmentColumnSpecs(bookmark).map(({title}) => title);

    // TODO(reviewer): we naively pull the segment names from the data. Should we have the api return it?
    // (the returns flattened segments, but maybe we should modify it to return the segments for each breakdown)
    // this doesn't work great if we have segments that have little overlap (i.e. country = US and cities = singapore, paris, etc)
    // TODO we should probably do a pass on the tree to find the largest segments or something similar, otherwise the
    // report doesn't really return the most interesting segments (just alphabetical will miss a lot of the good stuff for high cardinality)
    const segmentItems: SegmentSection[] = [];

    // do BFS to parse each level and add segments respectfully to generate the legends
    const q: Array<{node: SegmentedFunnelResults | FunnelStep[]; level: number}> = [{node: segmentData, level: 0}];
    const segments = {};
    while (q.length) {
      const {node, level} = q.shift();
      const keys = Object.keys(node).filter((s) => s !== `$overall` && s !== `$other`);

      if (!segments[labels[level]]) {
        segments[labels[level]] = new Set();
      }
      keys.forEach((key) => {
        segments[labels[level]].add(key);
      });
      q.push(...keys.filter((key) => !Array.isArray(node[key])).map((key) => ({node: node[key], level: level + 1})));
    }

    Object.keys(segments).forEach((label) => {
      segmentItems.push({
        label,
        items: Array.from(segments[label]).map((key: string) => ({
          id: key,
          label: key,
        })),
      });
    });

    let savedLegend: SavedLegend[] = null;
    if (bookmark?.savedLegend && bookmark?.savedLegend?.length > 0) {
      savedLegend = bookmark.savedLegend;
    } else if (bookmark?.selectedSegments?.length) {
      savedLegend = convertToNonFlatSavedLegend(bookmark.selectedSegments, collectNestedSegments(segmentData))?.map(
        (legendSection) => ({
          ...legendSection,
          selected: legendSection.selected.filter((legendItem) => legendItem.key !== OVERALL_PROP),
        }),
      );
    }

    return {
      // We reverse segment items so that the most segmented legend is the one at the top (which has colors)
      sections: segmentItems.reverse(),
      savedLegend,
    };
  }

  return {
    sections: [
      {
        items: [
          {
            id: OVERALL_PROP,
            label: OVERALL_PROP_NAME,
            color: colors.getChartSegmentColorCombo(0).primary, // Overall is always the first primary color
          },
        ],
      },
    ],
    savedLegend: null,
  };
}

export function getBarChartDataFromRawFunnel({
  funnelsResponse,
  bookmark,
  selectedSections,
}: {
  funnelsResponse: FunnelQueryResponse;
  selectedSections: SegmentSection[];
  bookmark: FunnelBookmark;
}): Array<BarVizSegment<FunnelSegmentExtras>> {
  const hasTimeComparison = bookmarkHasTimeComparison(bookmark);
  const isLift = bookmarkHasLiftComparison(bookmark) && funnelsResponse.lift_data;

  const date = funnelsResponse.meta.dates[0];
  const segmentData = funnelsResponse.data[date];
  let compareSegments;
  if (hasTimeComparison) {
    compareSegments = Object.values(funnelsResponse.compare_data)[0];
  }

  if (isSegmentedData(segmentData)) {
    return generateBarsFromFunnelSegmentTree({
      segments: segmentData,
      compareSegments,
      liftSegments: isLift ? (funnelsResponse.lift_data[date] as SegmentedFunnelResults) : null,
      selectedSegmentKeys: selectedSections,
      bookmark,
      segmentPath: [],
    });
  }

  return generateLeafSegment({
    segment: segmentData.steps,
    compareSegment: bookmarkHasTimeComparison(bookmark)
      ? (Object.values(funnelsResponse.compare_data)[0] as UnsegmentedFunnelResult)?.steps
      : null,
    liftSegment: isLift ? (Object.values(funnelsResponse.lift_data)[0] as UnsegmentedFunnelResult)?.steps : null,
    color: colors.getChartSegmentColorCombo(0).primary,
    bookmark,
    segmentLabel: OVERALL_PROP_NAME,
    segmentPath: [{id: OVERALL_PROP, label: OVERALL_PROP_NAME}],
  });
}

/**
 * Modifies the funnels reponse in place, adding conversion rates to all of the leaf-level steps
 * TODO(sam) - eventually refactor `addConversionToFunnelStep` to not modify in place, and instead
 * return a new funnel step typed with conversion rate
 * It was left as is for now to not have to refactor more funnels code than necessary
 */
export function addConversionRateToFunnel(
  funnelsResponse: FunnelQueryResponse,
  bookmark: FunnelBookmark,
): FunnelQueryResponse {
  const date = funnelsResponse.meta.dates[0];

  const segmentData = funnelsResponse.data[date];

  const q: Array<SegmentedFunnelResults | UnsegmentedFunnelResult> = [segmentData];

  if (bookmarkHasTimeComparison(bookmark)) {
    q.push(Object.values(funnelsResponse.compare_data)[0]);
  }
  if (bookmarkHasLiftComparison(bookmark)) {
    q.push(Object.values(funnelsResponse.lift_data)[0]);
  }

  while (q.length) {
    const curr = q.pop();

    if (isArray(curr)) {
      // leaf node with steps, add conversion to them
      curr.map(addConversionRateToFunnelStep);
    } else {
      const segments = Object.keys(curr).map((key) => curr[key]);

      q.push(...segments);
    }
  }

  return funnelsResponse;
}

export function createBarChartPopoverSelection({
  segment,
  barSegment,
  pageX,
  pageY,
  x,
  y,
  scrollX,
  scrollY,
}: {
  segment: BarVizSegment<FunnelSegmentExtras & BucketValueInfo>;
  barSegment: BarSegment<FunnelSegmentExtras & BucketValueInfo>;
  pageX: number;
  pageY: number;
  x: number;
  y: number;
  scrollX: number;
  scrollY: number;
}) {
  const segmentPath = segment.extras.segmentPath;

  const gap = 16;
  const left = pageX - x - scrollX + gap;
  const top = pageY - y - scrollY + gap;

  // n.b. we have to do a little bit of work to match what funnels usually receives from the old bar chart.
  // so we construct what it's expecting for the name/label (of a flattened segment), and match the shape
  // that it expects.
  const selection = {
    queryIndex: barSegment.extras.bucketIdx,
    segment: {
      name: segmentPath.map((segment) => segment.id).join(` / `),
      label: segmentPath.map((segment) => segment.label).join(` / `),
      count: barSegment.extras.count,
      color: barSegment.color,
    },
    isComparison: barSegment.extras.isComparison,
    rowLabel: segment.label,
    clientX: left,
    clientY: top,
  };
  return selection;
}
